import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { dataService, setLanguage as setServiceLanguage } from '../services/dataService';
import { translateUI } from '../i18n/ui';

const LanguageCtx = createContext(null);
export const useLanguage = () => useContext(LanguageCtx);

const STORAGE_KEY = 'paelbi_lang';
const SUPPORTED = ['it', 'en', 'de', 'fr'];

// hex "#8B6F4E" -> "139 111 78" (RGB channels for CSS variables)
function hexToChannels(hex) {
  if (!hex || typeof hex !== 'string') return null;
  const m = hex.replace('#', '').match(/^([0-9a-f]{6})$/i);
  if (!m) return null;
  const int = parseInt(m[1], 16);
  return `${(int >> 16) & 255} ${(int >> 8) & 255} ${int & 255}`;
}

function resolveThemeColors(theme, custom) {
  const bg2 = theme.bg2 || custom.secondary;
  return {
    '--paelbi-bg': theme.bg,
    '--paelbi-bg2': bg2,
    '--paelbi-accent': theme.accent || custom.accent,
    '--paelbi-brown': theme.brown || custom.primary,
    '--paelbi-ink': theme.ink,
    // line/badge tone follows bg2 for visual consistency
    '--paelbi-line': bg2,
  };
}

function applyCssVars(root, colors) {
  Object.entries(colors).forEach(([varName, hex]) => {
    const ch = hexToChannels(hex);
    if (ch) root.style.setProperty(varName, ch);
  });
}

function applyTheme(tenant) {
  if (!tenant) return;
  const custom = (tenant.settings && tenant.settings.theme) || {};
  applyCssVars(document.documentElement, resolveThemeColors(tenant.theme || {}, custom));
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    const initial = SUPPORTED.includes(saved) ? saved : 'it';
    // Sync the data service synchronously on first render so child pages
    // fetch content in the correct language before the effect below runs.
    setServiceLanguage(initial);
    return initial;
  });
  const [config, setConfig] = useState(null);

  const setLang = useCallback((next) => {
    if (!SUPPORTED.includes(next)) return;
    localStorage.setItem(STORAGE_KEY, next);
    setServiceLanguage(next);  // keep data service in sync synchronously so pages refetch correctly
    setLangState(next);
  }, []);

  // Keep the data service in sync with the active language.
  useEffect(() => {
    setServiceLanguage(lang);
    document.documentElement.lang = lang;
  }, [lang]);

  // Load tenant config once and apply branding/theme.
  const loadConfig = useCallback(() => {
    return dataService.getConfig().then((cfg) => {
      setConfig(cfg);
      applyTheme(cfg.tenant);
      if (cfg.tenant?.brand?.name) {
        document.title = `${cfg.tenant.brand.name} · ${cfg.tenant.brand.payoff || ''}`.trim();
      }
      return cfg;
    }).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    dataService.getConfig().then((cfg) => {
      if (!active) return;
      setConfig(cfg);
      applyTheme(cfg.tenant);
      if (cfg.tenant?.brand?.name) {
        document.title = `${cfg.tenant.brand.name} · ${cfg.tenant.brand.payoff || ''}`.trim();
      }
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const t = useCallback((text) => translateUI(lang, text), [lang]);

  const value = useMemo(() => ({ lang, setLang, t, languages: config?.tenant?.languages || SUPPORTED, config, reloadConfig: loadConfig }), [lang, setLang, t, config, loadConfig]);

  return <LanguageCtx.Provider value={value}>{children}</LanguageCtx.Provider>;
}

export default LanguageProvider;
